"use strict";
/**
 * @file number input
 * @description 用于整数输入的组件
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.sub = exports.add = exports.parse = exports.isValidText = exports.isNumberText = void 0;
var tslib_1 = require("tslib");
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var react_1 = tslib_1.__importStar(require("react"));
var icons_1 = require("../icons");
var utils_1 = require("../utils");
var hooks_1 = require("../utils/hooks");
var ConfigProvider_1 = require("../ConfigProvider");
var TextInput_1 = tslib_1.__importDefault(require("./TextInput"));
function NumberInput(_a) {
    var valueFromProps = _a.value, _b = _a.defaultValue, defaultValue = _b === void 0 ? null : _b, onChange = _a.onChange, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.min, min = _d === void 0 ? Number.NEGATIVE_INFINITY : _d, _e = _a.max, max = _e === void 0 ? Number.POSITIVE_INFINITY : _e, _f = _a.digits, digits = _f === void 0 ? 0 : _f, _g = _a.step, step = _g === void 0 ? 1 / (Math.pow(10, digits)) : _g, suffix = _a.suffix, _h = _a.emptyValue, emptyValue = _h === void 0 ? defaultValue : _h, inputProps = _a.inputProps, otherProps = tslib_1.__rest(_a, ["value", "defaultValue", "onChange", "disabled", "min", "max", "digits", "step", "suffix", "emptyValue", "inputProps"]);
    var initialValue = valueFromProps !== undefined ? valueFromProps : defaultValue;
    var _j = react_1.useState(stringify(initialValue, digits)), textValue = _j[0], setTextValue = _j[1];
    var _k = react_1.useState(initialValue), innerValue = _k[0], setInnerValue = _k[1];
    var value = valueFromProps !== undefined ? valueFromProps : innerValue;
    var _l = hooks_1.useFocused(inputProps), focused = _l.focused, focusedHandlers = _l.focusedHandlers;
    // 同步值（value）到界面（textValue）
    var syncValue = react_1.useCallback(function (v) {
        if (v === void 0) { v = value; }
        setTextValue(stringify(v, digits));
    }, [setTextValue, value, digits]);
    react_1.useEffect(function () {
        if (!focused)
            syncValue();
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
    var setValue = react_1.useCallback(function (v) {
        if (v != null && v > max)
            v = max;
        if (v != null && v < min)
            v = min;
        if (v !== innerValue)
            setInnerValue(v);
        if (v !== value)
            onChange === null || onChange === void 0 ? void 0 : onChange(v);
        return v;
    }, [innerValue, onChange, value, min, max]);
    var inputClassName = ConfigProvider_1.usePrefix('number-input');
    var className = classnames_1.default(inputClassName, 
    // 若没有自定义 suffix，只有 ctrl 放在 TextInput 的 suffix 里
    // 因为 ctrl 是绝对定位，这时需要把 TextInput suffix 的 margin 干掉
    suffix == null && utils_1.concat(inputClassName, 'no-suffix'), otherProps.className);
    var increaseDisabled = disabled || (value != null && value >= max);
    var decreaseDisabled = disabled || (value != null && value <= min);
    var handleIncrease = react_1.useCallback(function () {
        if (increaseDisabled)
            return;
        var newValue = setValue(add(value !== null && value !== void 0 ? value : 0, step, digits));
        // 注意这里需要手动做下 value 到 text value 的同步，因为 value 到 text value 的自动同步（见上方 effect）
        // 只在非输入（focus）的状态下保持，而 TextInput keyUp 导致的 increase 发生在 focus 状态下，
        // 此时需要这里的同步来让用户看到 value 发生了 increase / decrease
        syncValue(newValue);
    }, [increaseDisabled, setValue, value, step, digits, syncValue]);
    var handleDecrease = react_1.useCallback(function () {
        if (decreaseDisabled)
            return;
        var newValue = setValue(sub(value !== null && value !== void 0 ? value : 0, step, digits));
        syncValue(newValue); // 这里需要 syncValue 的原因同上边 handleIncrease
    }, [decreaseDisabled, setValue, value, step, digits, syncValue]);
    var increaseClassName = classnames_1.default(utils_1.concat(inputClassName, 'ctrl-increase'), increaseDisabled && utils_1.concat(inputClassName, 'ctrl-disabled'));
    var decreaseClassName = classnames_1.default(utils_1.concat(inputClassName, 'ctrl-decrease'), decreaseDisabled && utils_1.concat(inputClassName, 'ctrl-disabled'));
    var suffixView = (react_1.default.createElement(react_1.default.Fragment, null,
        suffix,
        react_1.default.createElement("div", { className: utils_1.concat(inputClassName, 'ctrl') },
            react_1.default.createElement("div", { className: increaseClassName, onClick: handleIncrease },
                react_1.default.createElement(icons_1.UpIcon, { className: utils_1.concat(inputClassName, 'ctrl-icon') })),
            react_1.default.createElement("div", { className: decreaseClassName, onClick: handleDecrease },
                react_1.default.createElement(icons_1.DownIcon, { className: utils_1.concat(inputClassName, 'ctrl-icon') })))));
    var handleChange = react_1.useCallback(function (v) {
        setTextValue(v);
        if (v === '')
            setValue(emptyValue);
        else if (isValidText(v, digits))
            setValue(parse(v, digits));
    }, [setValue, emptyValue, digits]);
    var handleBlur = react_1.useCallback(function (e) {
        focusedHandlers.onBlur(e);
        syncValue();
    }, [focusedHandlers, syncValue]);
    var keyboardHandlers = useKeyboard(handleIncrease, handleDecrease, inputProps);
    return (react_1.default.createElement(TextInput_1.default, tslib_1.__assign({}, otherProps, { inputProps: tslib_1.__assign(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, inputProps), focusedHandlers), keyboardHandlers), { onBlur: handleBlur }), className: className, value: textValue, onChange: handleChange, disabled: disabled, suffix: suffixView })));
}
exports.default = NumberInput;
var numberPattern = /^-?\d+(\.\d+)?$/;
function isNumberText(v) {
    return numberPattern.test(v);
}
exports.isNumberText = isNumberText;
function isValidText(v, digits) {
    return isNumberText(v) && parse(v, digits) === parseFloat(v);
}
exports.isValidText = isValidText;
function parse(v, digits) {
    var rawNum = parseFloat(v);
    return parseFloat(rawNum.toFixed(digits));
}
exports.parse = parse;
function stringify(v, digits) {
    return v != null ? v.toFixed(digits) : '';
}
// 对键盘操作进行响应的逻辑
function useKeyboard(onIncrease, onDecrease, inputProps) {
    var handleKeyDown = react_1.useCallback(function (e) {
        var _a;
        (_a = inputProps === null || inputProps === void 0 ? void 0 : inputProps.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(inputProps, e);
        if (e.defaultPrevented)
            return;
        switch (e.key) {
            case 'Down': // IE/Edge specific value
            case 'ArrowDown':
                e.preventDefault();
                onDecrease();
                break;
            case 'Up': // IE/Edge specific value
            case 'ArrowUp':
                e.preventDefault();
                onIncrease();
                break;
            default:
        }
    }, [onIncrease, onDecrease, inputProps]);
    return { onKeyDown: handleKeyDown };
}
// 特别实现 add & sub，以避免 javascript 浮点数精度导致的 Round-off errors（如 0.1 + 0.2 = 0.30000000000000004）
// 注意这里基于 `number` & `by` 的小数位数都不会超过 `digits` 值的假设，简单地处理了下这个问题
function add(num, by, digits) {
    var factor = Math.pow(10, digits);
    return Math.round(num * factor + by * factor) / factor;
}
exports.add = add;
function sub(num, by, digits) {
    var factor = Math.pow(10, digits);
    return Math.round(num * factor - by * factor) / factor;
}
exports.sub = sub;
//# sourceMappingURL=NumberInput.js.map